import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  buildJobDetailPath,
  getJobSearchSeo,
  isNonProductionIndexingHost,
  isPrivateIndexingRoute,
  NEXT_HIRE_ORIGIN
} from "app/utils/jobSearchSeo";

const DEFAULT_DESCRIPTION =
  "NextHire is an online job portal and recruitment software in India with ATS, candidate management, employer portal, job posting, resume tracking, interview management, recruitment CRM, analytics, and invoicing tools.";

const DEFAULT_KEYWORDS = [
  "Recruitment Software",
  "Applicant Tracking System",
  "ATS Software",
  "Recruitment Management System",
  "Hiring Software",
  "Candidate Management System",
  "Recruitment CRM",
  "Job Portal Software",
  "HR Recruitment Software",
  "AI Recruitment Software",
  "Candidate Management Software",
  "Employer Management Software",
  "Online Job Posting Software",
  "Resume Management Software",
  "Candidate Tracking Software",
  "Recruitment Pipeline Software",
  "Interview Management Software",
  "Recruitment Invoice Software",
  "Employee Management Software",
  "Employer Portal Software",
  "Recruiter Management Software",
  "Online Job Portal",
  "Career Portal Software",
  "Recruitment Analytics Software",
  "Hiring Workflow Software",
  "Best Recruitment Software in India",
  "Best Applicant Tracking System",
  "AI Recruitment Software for Companies",
  "Recruitment Software for Small Business",
  "Online Hiring Platform",
  "Candidate Tracking System",
  "Job Posting Software",
  "Resume Screening Software",
  "Employee Recruitment Platform",
  "HR Hiring Software",
  "Hiring Management Software",
  "Recruitment CRM Software",
  "Recruitment Automation Platform",
  "Interview Scheduling Software",
  "Applicant Tracking Software for Recruiters",
  "Job Portal Software for Recruitment Agencies",
  "Online Recruitment Management System",
  "Hiring Platform for Startups",
  "End-to-End Recruitment Software",
  "Cloud-Based Recruitment Software",
  "Recruitment Software India",
  "ATS Software India",
  "Hiring Software India",
  "Job Portal India",
  "HR Software India",
  "Recruitment CRM India",
  "Online Hiring Platform India",
  "Best ATS in India",
  "ATS Software Alternative",
  "Recruitment Software Alternative",
  "Best Hiring Software",
  "Top Recruitment Software",
  "Recruitment Software Comparison",
  "Recruitment Automation Software",
  "Candidate Management",
  "Job Posting",
  "Interview Management",
  "Employee Management",
  "Employer Portal",
  "Buy Recruitment Software",
  "Recruitment Software Demo",
  "ATS Software Free Trial",
  "Recruitment Software Pricing",
  "Recruitment Software for HR",
  "Best Hiring Platform",
  "Recruitment Software for Recruitment Agencies",
  "Online Recruitment Platform",
  "Recruitment Software with Job Portal",
  "Recruitment Software with Candidate Tracking"
].join(", ");

const ROUTE_DESCRIPTIONS = {
  "/": DEFAULT_DESCRIPTION,
  "/jobs": "Browse the latest job openings on NextHire and find roles that match your skills.",
  "/privacyPolicy": "Read the NextHire privacy policy and learn how your data is handled.",
  "/dashboard/default":
    "View your NextHire hiring dashboard, recruitment pipeline, applications, interviews, jobs, and candidate management activity.",
  "/applications/current": "Track current job applications and candidate progress on NextHire.",
  "/applications/detail": "Review application details, candidate status, and hiring information.",
  "/jobs/apply": "Complete your job application on NextHire.",
  "/interviews/list": "View scheduled interviews and interview updates on NextHire.",
  "/jobs/listings":
    "Manage and browse job listings using NextHire job portal software and online job posting tools.",
  "/jobs/job-apply": "Apply to selected job opportunities on NextHire.",
  "/jobs/candidate/applied": "Track your submitted job applications and application status.",
  "/jobs/company/applied":
    "Review candidates, application status, and hiring workflow activity in NextHire applicant tracking software.",
  "/jobs/postjob":
    "Post jobs online and hire qualified candidates with NextHire recruitment software.",
  "/candidate/listings":
    "Browse and manage candidate profiles with NextHire candidate management software.",
  "/candidate/detail": "View candidate profile details, skills, and application information.",
  "/candidate/addCandidate": "Add a new candidate profile to NextHire.",
  "/company/listings":
    "Browse and manage employer profiles with NextHire employer management software.",
  "/company/addCompany": "Add a new company profile to NextHire.",
  "/profile": "Manage your NextHire profile, resume, account details, and preferences.",
  "/invoice/create": "Create a new recruitment invoice in NextHire.",
  "/invoice": "View and manage NextHire recruitment invoices and billing records.",
  "/report": "View recruitment analytics, payment reports, and hiring performance in NextHire.",
  "/skill-management": "Manage skills used across jobs and candidate profiles on NextHire.",
  "/benefit-management": "Manage job benefits available for listings on NextHire.",
  "/checkout/payment": "Complete your NextHire checkout and payment.",
  "/trash": "View and restore deleted NextHire records.",
  "/session/login/admin": "Admin login for NextHire.",
  "/session/login/candidate": "NextHire - Online Job Portal for Candidates",
  "/session/login/company": "NextHire - Recruitment Software and ATS for Companies",
  "/session/signup/candidate": "NextHire - Online Job Portal for Candidates",
  "/session/signup/company":
    "Start hiring with NextHire recruitment software, ATS, recruitment CRM, and employer portal.",
  "/session/forgot-password": "Reset your NextHire account password."
};

const ROUTE_KEYWORDS = {
  "/jobs": "Online Job Portal, Job Portal India, Career Portal Software, Job Posting Software, Online Hiring Platform India, Job Portal Software",
  "/dashboard/default":
    "Recruitment Analytics Software, Hiring Workflow Software, Recruitment Pipeline Software, Candidate Management System, Applicant Tracking System",
  "/jobs/listings":
    "Online Job Posting Software, Job Posting Software, Job Portal Software, Employer Portal Software, Hiring Software",
  "/jobs/company/applied":
    "Applicant Tracking System, Candidate Tracking Software, Candidate Management Software, Resume Screening Software, Hiring Management Software",
  "/jobs/postjob":
    "Online Job Posting Software, Job Posting Software, Hiring Software India, Recruitment Software for HR",
  "/candidate/listings":
    "Candidate Management Software, Candidate Tracking System, Resume Management Software, Candidate Management",
  "/company/listings":
    "Employer Management Software, Employer Portal Software, Recruiter Management Software",
  "/invoice":
    "Recruitment Invoice Software, Recruitment Management System, Recruitment Analytics Software",
  "/report":
    "Recruitment Analytics Software, Recruitment Software Comparison, Hiring Workflow Software",
  "/session/login/company":
    "Recruitment Software, ATS Software, Applicant Tracking Software for Recruiters, Recruitment CRM Software",
  "/session/signup/company":
    "Buy Recruitment Software, Recruitment Software Demo, ATS Software Free Trial, Recruitment Software Pricing, Recruitment Software for Small Business, Hiring Platform for Startups"
};

function getRouteDescription(pathname) {
  if (pathname.startsWith("/jobs/job-detail")) {
    return "View job details, required skills, salary, company information, and apply on NextHire.";
  }

  if (
    pathname.startsWith("/company") ||
    pathname.startsWith("/session/login/company") ||
    pathname.startsWith("/session/signup/company")
  ) {
    return "NextHire - Simplify Your Hiring Journey";
  }

  const matchedRoute = Object.keys(ROUTE_DESCRIPTIONS)
    .filter((route) => pathname === route || pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];

  return matchedRoute ? ROUTE_DESCRIPTIONS[matchedRoute] : DEFAULT_DESCRIPTION;
}

function getRouteKeywords(pathname) {
  const matchedRoute = Object.keys(ROUTE_KEYWORDS)
    .filter((route) => pathname === route || pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0];

  return matchedRoute ? `${ROUTE_KEYWORDS[matchedRoute]}, ${DEFAULT_KEYWORDS}` : DEFAULT_KEYWORDS;
}

export default function RouteMeta() {
  const { pathname, search } = useLocation();
  const jobSearchSeo = getJobSearchSeo(pathname, search);
  const jobDetailMatch = pathname.match(/^\/jobs\/job-detail\/([^/]+)\/([^/]+)$/);
  const jobDetailCanonicalPath = jobDetailMatch
    ? buildJobDetailPath(decodeURIComponent(jobDetailMatch[1]), jobDetailMatch[2])
    : "";
  const isPrivateRoute = isPrivateIndexingRoute(pathname);
  const isNonProductionHost = isNonProductionIndexingHost(window.location.hostname);
  const description = jobSearchSeo?.description || getRouteDescription(pathname);
  const keywords = jobSearchSeo?.keywords || getRouteKeywords(pathname);
  const canonicalUrl =
    jobSearchSeo?.canonicalUrl ||
    `${NEXT_HIRE_ORIGIN}${jobDetailCanonicalPath || pathname}`;
  const robots =
    isPrivateRoute || isNonProductionHost
      ? "noindex, nofollow"
      : jobSearchSeo?.robots || "index, follow";

  useEffect(() => {
    if (jobSearchSeo?.title) {
      document.title = jobSearchSeo.title;
      updateMetaTag('meta[property="og:title"]', "property", "og:title", jobSearchSeo.title);
      updateMetaTag('meta[name="twitter:title"]', "name", "twitter:title", jobSearchSeo.title);
    }

    updateMetaTag('meta[name="description"]', "name", "description", description);
    updateMetaTag('meta[name="keywords"]', "name", "keywords", keywords);
    updateMetaTag('meta[name="robots"]', "name", "robots", robots);
    updateMetaTag('meta[property="og:description"]', "property", "og:description", description);
    updateMetaTag('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    updateMetaTag('meta[name="twitter:description"]', "name", "twitter:description", description);
    updateLinkTag("canonical", canonicalUrl);
  }, [canonicalUrl, description, jobSearchSeo?.title, keywords, robots]);

  return null;
}

function updateLinkTag(rel, href) {
  const links = document.head.querySelectorAll(`link[rel="${rel}"]`);
  const link = links[0] || document.createElement("link");

  link.setAttribute("rel", rel);
  link.setAttribute("href", href);

  if (!links.length) {
    document.head.appendChild(link);
  }

  links.forEach((duplicateLink, index) => {
    if (index > 0) duplicateLink.remove();
  });
}

function updateMetaTag(selector, attributeName, attributeValue, content) {
  const tags = document.head.querySelectorAll(selector);
  const tag = tags[0] || document.createElement("meta");

  tag.setAttribute(attributeName, attributeValue);
  tag.setAttribute("content", content);

  if (!tags.length) {
    document.head.appendChild(tag);
  }

  tags.forEach((duplicateTag, index) => {
    if (index > 0) {
      duplicateTag.remove();
    }
  });
}
